import Box from '@mui/material/Box';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CategoryIcon from '@mui/icons-material/Category';
import GetAppIcon from '@mui/icons-material/GetApp';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button, Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import { getSecurityNavItemPath, SECURITY_ENDPOINTS_HREF } from 'src/mindtime/config/securityNav';
import { SECURITY_PAGE_FILTER_KEYS } from 'src/mindtime/constants/securityFilters';
import { useSecurityEdrToolbarReset, useSecurityPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import {
  useWazuhScope,
  WAZUH_DATE_RANGE_OPTIONS,
  type WazuhDateRangeOption,
} from 'src/mindtime/hooks/useWazuhScope';
import type { Column, TableData } from 'src/types/table';
import type { WazuhThreatHuntingFindingItem } from 'src/types/mindtime/wazuhThreatHunting';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrDateTime,
  EdrKpiCard,
  EdrPageHeader,
  EdrSeverityPill,
  EdrTruncatedText,
  edrMonoStyle,
  edrMutedStyle,
  type EdrSeverityLevel,
  type EdrToolbarFilter,
} from '../shared/edrPrimitives';
import { useThreatHuntingData, useThreatHuntingExport } from './useThreatHuntingData';

const breadcrumb = {
  links: [
    { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
    { name: 'Threat Hunting', to: getSecurityNavItemPath('threat-hunting') ?? '' },
  ],
};

const SEVERITY_OPTIONS = [
  { label: 'Critical', value: 'critical' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

const DATE_RANGE_OPTIONS = WAZUH_DATE_RANGE_OPTIONS.map((option) => ({
  label: option,
  value: option,
}));

const formatNumber = (value: number | undefined | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

const ThreatHunting: FC = () => {
  const { dateRange, setDateRange, isReseller } = useWazuhScope();
  const { filters, setFilter, search, searchInput, setSearchInput, resetFilters } =
    useSecurityPageFilters(SECURITY_PAGE_FILTER_KEYS.threatHunting);
  const {
    severityBand = '',
    organizationId = '',
    wazuhAgentId = '',
    user = '',
    mitreTechnique = '',
    eventType = '',
  } = filters;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const huntingFilters = useMemo(
    () => ({
      user: filters.user,
      mitreTechnique: filters.mitreTechnique,
      eventType: filters.eventType,
      severityBand: filters.severityBand,
      wazuhAgentId: filters.wazuhAgentId,
      organizationId: filters.organizationId,
    }),
    [filters]
  );

  const { loading, summary, items: paged, facets } = useThreatHuntingData(
    page,
    rowsPerPage,
    search,
    huntingFilters
  );
  const { exporting, handleExport } = useThreatHuntingExport(search, huntingFilters);

  const items = paged?.items ?? [];
  const totalRecords = paged?.totalRecords ?? 0;

  const handleResetFilters = useSecurityEdrToolbarReset(resetFilters, setDateRange, setPage);

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'user',
        label: 'User',
        icon: <PersonIcon style={{ fontSize: 14 }} />,
        options: facets.users.map((name) => ({ label: name, value: name })),
        value: user,
        onChange: (value) => {
          setFilter('user', value);
          setPage(0);
        },
      },
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: SEVERITY_OPTIONS,
        value: severityBand,
        onChange: (value) => {
          setFilter('severityBand', value);
          setPage(0);
        },
      },
      {
        id: 'mitre',
        label: 'MITRE Technique',
        icon: <SecurityIcon style={{ fontSize: 14 }} />,
        options: facets.mitreIds.map((id) => ({ label: id, value: id })),
        value: mitreTechnique,
        onChange: (value) => {
          setFilter('mitreTechnique', value);
          setPage(0);
        },
      },
      {
        id: 'eventType',
        label: 'Event Type',
        icon: <CategoryIcon style={{ fontSize: 14 }} />,
        options: facets.eventTypes.map((name) => ({ label: name, value: name })),
        value: eventType,
        onChange: (value) => {
          setFilter('eventType', value);
          setPage(0);
        },
      },
      ...(isReseller
        ? [
            {
              id: 'org',
              label: 'Organization',
              variant: 'resellerClient' as const,
              value: organizationId,
              onChange: (value: string) => {
                setFilter('organizationId', value);
                setFilter('wazuhAgentId', '');
                setPage(0);
              },
            },
          ]
        : []),
      {
        id: 'endpoint',
        label: 'Endpoint',
        variant: 'searchableEndpoint' as const,
        pageOrganizationId: organizationId || undefined,
        value: wazuhAgentId,
        onChange: (value: string) => {
          setFilter('wazuhAgentId', value);
          setPage(0);
        },
      },
      {
        id: 'date',
        label: 'Date Range',
        icon: <CalendarTodayIcon style={{ fontSize: 14 }} />,
        options: DATE_RANGE_OPTIONS,
        value: dateRange,
        onChange: (value) => {
          setDateRange(value as WazuhDateRangeOption);
          setPage(0);
        },
      },
    ],
    [
      user,
      severityBand,
      mitreTechnique,
      eventType,
      organizationId,
      wazuhAgentId,
      dateRange,
      isReseller,
      facets.users,
      facets.mitreIds,
      facets.eventTypes,
      setFilter,
      setDateRange,
    ]
  );

  const columns: Column[] = useMemo(
    () => [
      {
        id: 'endpoint',
        label: 'Endpoint',
        render: (row) => (
          <EdrTruncatedText
            value={(row as unknown as WazuhThreatHuntingFindingItem).endpoint || '—'}
            style={edrMonoStyle}
            maxWidth={160}
          />
        ),
      },
      {
        id: 'organization',
        label: 'Organization',
        render: (row) => (
          <EdrTruncatedText
            value={(row as unknown as WazuhThreatHuntingFindingItem).organization || '—'}
            style={edrMutedStyle}
            maxWidth={160}
          />
        ),
      },
      {
        id: 'detection',
        label: 'Detection',
        render: (row) => (
          <EdrTruncatedText
            value={(row as unknown as WazuhThreatHuntingFindingItem).detection || '—'}
            style={{ fontWeight: 600, color: EDR_COLORS.textPrimary }}
            maxWidth={280}
          />
        ),
      },
      {
        id: 'mitreTechnique',
        label: 'MITRE ATT&CK',
        render: (row) => (
          <span style={{ ...edrMonoStyle, color: EDR_COLORS.brand600, fontWeight: 600 }}>
            {(row as unknown as WazuhThreatHuntingFindingItem).mitreId || '—'}
          </span>
        ),
      },
      {
        id: 'user',
        label: 'User',
        render: (row) => (
          <span style={edrMonoStyle}>
            {(row as unknown as WazuhThreatHuntingFindingItem).user || '—'}
          </span>
        ),
      },
      {
        id: 'severity',
        label: 'Severity',
        render: (row) => (
          <EdrSeverityPill
            level={(row as unknown as WazuhThreatHuntingFindingItem).severity as EdrSeverityLevel}
          />
        ),
      },
      {
        id: 'timestamp',
        label: 'Timestamp',
        render: (row) => (
          <EdrDateTime value={(row as unknown as WazuhThreatHuntingFindingItem).timestamp} />
        ),
      },
    ],
    []
  );

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — Threat Hunting')}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ gap: 16, minHeight: '100%', boxSizing: 'border-box' }}
      >
        <EdrPageHeader
          breadcrumb={breadcrumb}
          title="Threat Hunting"
          subtitle="Search, investigate, and analyze endpoint activity to identify potential threats before they become incidents."
          actions={(
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              style={{ textTransform: 'none' }}
              loading={exporting}
              onClick={handleExport}
            >
              Export Results
            </Button>
          )}
        />

        <Box
          display="grid"
          style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}
        >
          <EdrKpiCard
            label="Active Hunts"
            value={formatNumber(summary?.activeHunts)}
            icon={<VisibilityIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.brand500}
          />
          <EdrKpiCard
            label="Threat Matches"
            value={formatNumber(summary?.threatMatches)}
            icon={<GpsFixedIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.critical}
          >
            Detected today
          </EdrKpiCard>
        </Box>

        <EdrDataCard
          toolbar={(
            <EdrDataToolbar
              searchValue={searchInput}
              onSearchChange={(value) => {
                setSearchInput(value);
                setPage(0);
              }}
              searchPlaceholder="Search Results…"
              filters={toolbarFilters}
              onReset={handleResetFilters}
            />
          )}
        >
          <Table
            columns={columns}
            data={items as unknown as TableData[]}
            loading={loading}
            showPagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRecords}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              setPage(0);
            }}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          />
        </EdrDataCard>
      </Box>
    </>
  );
};

export default ThreatHunting;
